import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
    NodePolygon,
    RecordDto,
    RecordDtoWithPolyogns,
} from "src/app/models/recordDto.model";
import {
    NodeRecordsRequestModel,
    WindDustDistribution,
} from "src/app/models/nodeRecords.model";

import { CommonService } from "src/app/services/common.service";
import { DateFilterTypeEnum } from "src/app/enums/dateFilterType.enum";
import { DateRangeModel } from "src/app/models/dateRange.model";
import { FilterRequest } from "src/app/models/filterRequest.model";
import { MapLibreRealtimeMapComponent } from "../../common-components/maplibre-realtime-map/maplibre-realtime-map.component";
import { HeatMap } from "src/app/models/heatmap.model";
import { LiveOption } from "src/app/models/liveOption.model";
import { NodeDetailsPopupComponent } from "../../common-components/node-details-popup/node-details-popup.component";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { ParameterLookup } from "src/app/models/parameter.model";
import { SensorRecordService } from "src/app/services/sensorRecord.service";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { ToastService } from "src/app/services/toast.service";
import { leftSlideInAnimation } from "src/app/animations/leftSlideIn.animation";
import { rightSlideInAnimation } from "src/app/animations/rightSlideIn.animation";
import { LoaderService } from "src/app/services/loader.service";
import { FlyoutContainerComponent } from "src/app/shared/flyout/flyout-container/flyout-container.component";
import { MapViewSettingsService } from "src/app/services/mapViewSettings.service";

import { trackByIndex } from "src/app/shared/utils/track-by.util";
import { hasMapLocation } from "src/app/shared/utils/dashboard-nodes.util";
@Component({
  standalone: false,
    selector: "app-real-time-monitoring-dashboard",
    templateUrl: "./real-time-monitoring-dashboard.component.html",
    styleUrls: ["./real-time-monitoring-dashboard.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [rightSlideInAnimation, leftSlideInAnimation],
})
export class RealTimeMonitoringDashboardComponent implements OnInit, OnDestroy {
    //TODO: refactor this component
    @ViewChild("realtimeMap") realtimeMap: MapLibreRealtimeMapComponent;
    @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

    loading: boolean = false;
    nodeSubscription: Subscription;
    payload: DateRangeModel;
    intervalId: any;
    selectedOrg: OrganizationLevel;
    selectedParameter: ParameterLookup;
    nodes: RecordDtoWithPolyogns[] | RecordDto[] = [];
    selectedNode: RecordDtoWithPolyogns | RecordDto;
    heatmapData: HeatMap[] = [];
    heatmapImage: string = "";
    showHeatmap: boolean = false;

    subscriptions: Subscription[] = [];
    /* A slow response for a previously clicked node must not draw its cones on the new one. */
    private windDustSubscription?: Subscription;
    selectedDateRange: any;
    liveOption: LiveOption;
    initialLiveLoaded = false;
    noDataFound = false;

    constructor(
        private sensorRecordService: SensorRecordService,
        private commonService: CommonService,
        private changeDetectorRef: ChangeDetectorRef,
        private loaderService: LoaderService,
        private mapViewSettingsService: MapViewSettingsService,
        private route: ActivatedRoute,
        private toastService: ToastService,
    ) {}

    ngOnInit() {
        this.restoreHeatmapSetting();
        this.loadHeaderData();
        this.subscriptions.push(
            this.commonService.liveOptionSubject
                .pipe(debounceTime(100))
                .subscribe((res) => {
                    this.liveOption = res;
                    if (!this.liveOption?.enabled && this.selectedParameter)
                        this.getAverageParameterValues();
                    else this.getLatestNodeRecords();
                }),
        );
    }

    getLatestNodeRecords(centerMap: boolean = true) {
        if (this.selectedParameter?.id) {
            this.loaderService.show();
            this.noDataFound = false;
            let payload: NodeRecordsRequestModel = {
                orgLevelId: this.selectedOrg?.id,
                analyteId: String(this.selectedParameter?.id),
            };

            if (this.nodeSubscription) this.nodeSubscription.unsubscribe();
            let req = this.sensorRecordService.getLatestNodeRecords(payload);
            if (this.showHeatmap)
                req =
                    this.sensorRecordService.getLatestNodeRecordsWithHeatmap(
                        payload,
                    );
            this.nodeSubscription = req.subscribe(
                (res: any) => {
                    if (!this.nodes.length) centerMap = true;
                    this.nodes = [];
                    if (this.intervalId) clearInterval(this.intervalId);
                    this.heatmapData = [];
                    this.heatmapImage = "";
                    let nodes: RecordDto[] = [];
                    if (!this.showHeatmap) {
                        nodes = res;
                    } else {
                        nodes = res?.sensorRecords;
                        if (
                            res?.heatMapData &&
                            res?.heatMapData?.latitude_bounds &&
                            res?.heatMapData?.longitude_bounds
                        ) {
                            this.heatmapImage =
                                res?.heatMapData?.heatmap_base64;
                            let i = 0;
                            res?.heatMapData?.latitude_bounds.forEach(
                                (latVal, latIndex) => {
                                    res?.heatMapData?.longitude_bounds.forEach(
                                        (lonVal, lonIndex) => {
                                            this.heatmapData.push({
                                                lat: latVal.toFixed(10),
                                                lng: lonVal.toFixed(10),
                                                weight:
                                                    res?.heatmapData
                                                        ?.sensor_readings[i] ||
                                                    null,
                                            });
                                            i++;
                                        },
                                    );
                                },
                            );
                        }
                    }

                    this.nodes = nodes.filter(hasMapLocation);
                    this.noDataFound = this.nodes.length === 0;
                    this.changeDetectorRef.detectChanges();
                    this.realtimeMap.getIndoorSections();

                    setTimeout(() => {
                        this.commonService.nodesUpdated.next(centerMap);
                        this.loaderService.hide();
                    }, 100);
                    this.intervalId = setInterval(() => {
                        this.getLatestNodeRecords(false);
                    }, 60000);
                    if (this.selectedNode) {
                        const currNode =
                            this.nodes.find(
                                (node) =>
                                    node.endPointId ===
                                    this.selectedNode.endPointId,
                            ) || null;
                        if (currNode) {
                            this.openEndpointInfoFlyout(
                                this.selectedNode.endPointId,
                            );
                        }
                    }
                },
                (err) => {
                    this.nodes = [];
                    this.noDataFound = true;
                    setTimeout(() => {
                        this.commonService.nodesUpdated.next(false);
                        this.loaderService.hide();
                    }, 100);
                    console.log(err);
                },
            );
        }
    }

    openNodeDetails(index: number) {
        if (index <= -1) return;
        this.selectedNode = this.nodes[index];
        this.changeDetectorRef.detectChanges();

        this.openEndpointInfoFlyout(this.selectedNode.endPointId);

        if (!this.liveOption.enabled) {
            this.loadPolygonsData();
        }
    }

    loadPolygonsData() {
        const payload: WindDustDistribution = {
            dateFilterType:
                this.selectedDateRange?.id ?? DateFilterTypeEnum.AllDates,
            startDate: this.selectedDateRange?.start ?? 0,
            endDate: this.selectedDateRange?.end ?? 0,
            includeVideo: false,
            analyteId: String(this.selectedParameter.id),
            endPointId: this.selectedNode.endPointId,
            orgLevelId: this.selectedOrg.id,
        };
        this.windDustSubscription?.unsubscribe();
        this.windDustSubscription = this.sensorRecordService.getWindDustDistribution(payload).subscribe({
            next: (res) => {
                if (res) {
                    const records = res.sensorRecords;
                    const polygons: NodePolygon[] = [];
                    if (records.hasOwnProperty("windrcrds")) {
                        for (const [key, value] of Object.entries(
                            records["windrcrds"],
                        ) as any) {
                            if (key !== "overall_average")
                                polygons.push({
                                    color:
                                        this.commonService.getColorCode(
                                            [...this.commonService.parameterThresholds]
                                                .sort((left, right) => left.minValue - right.minValue)
                                                .find(
                                                    (threshold) =>
                                                        threshold.maxValue == null ||
                                                        value.average_pm_value <
                                                        threshold.maxValue,
                                                )?.colorHex ??
                                            this.commonService.colors.greyLight,
                                        ),
                                    parameterValue: value.average_pm_value,
                                    windSpeed: value.average_win_speed,
                                    windFrequency: value.win_frequency
                                        ? Number(value.win_frequency.toFixed(2))
                                        : 0,
                                    windAngleSpread1: Number(key.split("-")[0]),
                                    windAngleSpread2: Number(key.split("-")[1]),
                                });
                        }
                    }
                    this.selectedNode = {
                        ...this.selectedNode,
                        polygons: polygons,
                    };
                    this.realtimeMap.selectNode(this.selectedNode);
                }
            },
            error: (error) => {
                this.toastService.showError(error);
            },
        });
    }

    hideNodeDetailPopup() {
        this.selectedNode = null;
    }

    getAverageParameterValues() {
        this.loaderService.show();
        this.noDataFound = false;
        const payload: FilterRequest = {
            dateFilterType:
                this.selectedDateRange?.id ?? DateFilterTypeEnum.AllDates,
            startDate: this.selectedDateRange?.start ?? 0,
            endDate: this.selectedDateRange?.end ?? 0,
            analyteId: String(this.selectedParameter.id),
            orgLevelId: this.selectedOrg.id,
        };
        this.sensorRecordService.getAverageParameterValues(payload).subscribe(
            (res) => {
                this.nodes = [];
                res.forEach((node) => {
                    node.parameterValue = { ...node.parameterValue, color: node.color };
                    if (hasMapLocation(node)) this.nodes.push(node);
                });
                this.noDataFound = this.nodes.length === 0;
                this.loaderService.hide();
                this.commonService.nodesUpdated.next(true);
                if (this.intervalId) clearInterval(this.intervalId);
            },
            (error) => {
                this.nodes = [];
                this.noDataFound = true;
                this.loaderService.hide();
                this.commonService.nodesUpdated.next(false);
                console.log(error);
            },
        );
    }

    toggleHeatmap(changed) {
        this.showHeatmap = !this.showHeatmap;
        this.mapViewSettingsService.updateSetting("showHeatmap", this.showHeatmap);
        this.getLatestNodeRecords();
    }

    /**
     * Restore heatmap setting from localStorage
     */
    private restoreHeatmapSetting(): void {
        const settings = this.mapViewSettingsService.getSettings();
        this.showHeatmap = settings.showHeatmap;
    }

    setSelectedOrg(selectedOrg: OrganizationLevel) {
        if (this.selectedOrg?.id != selectedOrg.id) {
            this.selectedOrg = selectedOrg;
            this.loadInitialData();
        }
    }

    setSelectedParameter(selectedParameter: ParameterLookup) {
        this.selectedParameter = selectedParameter;
        this.loadInitialData();
    }

    setSelectedDateRange(selectedDateRange: any) {
        this.selectedDateRange = selectedDateRange;
        this.loadInitialData();
    }

    loadInitialData() {
        this.selectedNode = null;
        if (
            this.selectedParameter &&
            this.selectedOrg &&
            this.selectedDateRange?.start &&
            !this.initialLiveLoaded
        ) {
            this.initialLiveLoaded = true;
            setTimeout(() => {
                this.commonService.liveOptionSubject.next({
                    show: true,
                    enabled: true,
                });
            }, 10);
        } else if (this.initialLiveLoaded) {
            this.commonService.liveOptionSubject.next(this.liveOption);
        }
    }

    loadHeaderData() {
        this.subscriptions.push(
            this.commonService
                .selectedParameterAfterCitation(this.route.snapshot.queryParams.parameter, (error) => this.toastService.showError(error))
                .subscribe({
                    next: (parameter) => {
                        if (parameter) this.setSelectedParameter(parameter);
                    },
                }),
        );
        this.subscriptions.push(
            this.commonService.selectedOrgEmitter.subscribe((selectedOrg) => {
                if (selectedOrg) this.setSelectedOrg(selectedOrg);
            }),
        );
        this.subscriptions.push(
            this.commonService.selectedDateFilterEmitter.subscribe(
                (dateFilter) => {
                    this.setSelectedDateRange(dateFilter);
                },
            ),
        );
    }

    ngOnDestroy() {
        if (this.intervalId) clearInterval(this.intervalId);
        this.windDustSubscription?.unsubscribe();
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
        this.commonService.liveOptionSubject.next({
            show: false,
            enabled: false,
        });
    }

    openEndpointInfoFlyout(nodeId: string): void {
        this.flyout?.open(NodeDetailsPopupComponent, nodeId, "630px");
    }

    public trackByIndex = trackByIndex;
}
